
import React, { useState, useEffect } from 'react';
import { WifiOff, Wifi } from 'lucide-react';

// Error boundary wrapper for NetworkStatus
class NetworkStatusErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.warn('NetworkStatus error caught by boundary:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return null; // Don't render anything if there's an error
    }
    return this.props.children;
  }
}

// Safe hook wrapper that checks React dispatcher availability
const useSafeState = <T,>(initialState: T | (() => T)): [T, React.Dispatch<React.SetStateAction<T>>] => {
  try {
    // Check if React hooks are available
    if (typeof React === 'undefined' || !React.useState) {
      throw new Error('React hooks not available');
    }
    return React.useState(initialState);
  } catch (error) {
    console.warn('useSafeState fallback activated:', error);
    // Return a fallback state that doesn't crash
    const fallbackValue = typeof initialState === 'function' 
      ? (initialState as () => T)() 
      : initialState;
    return [fallbackValue, () => {}] as [T, React.Dispatch<React.SetStateAction<T>>];
  }
};

const NetworkStatusComponent: React.FC = () => {
  // Use safe state hook
  const [isOnline, setIsOnline] = useSafeState(() => {
    // Only check navigator if we're in browser
    if (typeof window === 'undefined' || typeof navigator === 'undefined') {
      return true; // Default to online if not in browser
    }
    
    try {
      return navigator.onLine;
    } catch (error) {
      console.warn('Error checking initial network state:', error);
      return true; // Default to online on error
    }
  });
  
  // Safe useEffect wrapper
  const useSafeEffect = (effect: React.EffectCallback, deps?: React.DependencyList) => {
    try {
      if (typeof React === 'undefined' || !React.useEffect) {
        console.warn('useEffect not available, skipping');
        return;
      }
      React.useEffect(effect, deps);
    } catch (error) {
      console.warn('useSafeEffect error:', error);
    }
  };

  useSafeEffect(() => {
    // Ensure we're in a browser environment and React is fully initialized
    if (typeof window === 'undefined' || typeof navigator === 'undefined') {
      console.warn('NetworkStatus: Not in browser environment');
      return;
    }

    const handleOnline = () => {
      console.log('Network status: Online');
      setIsOnline(true);
      
      // Update offline container visibility safely
      try {
        document.body.classList.remove('offline');
      } catch (error) {
        console.warn('Error updating body class:', error);
      }
      
      // Request sync from service worker
      if ('serviceWorker' in navigator) {
        navigator.serviceWorker.ready.then((registration) => {
          const reg = registration as any;
          
          if (reg.sync && 'SyncManager' in window) {
            try {
              reg.sync.register('sync-data')
                .then(() => console.log('[NetworkStatus] Background sync registered'))
                .catch((err: Error) => {
                  console.error('[NetworkStatus] Error registering background sync:', err);
                });
            } catch (e) {
              console.log('[NetworkStatus] Background sync not supported in this browser');
            }
          }
        }).catch(error => {
          console.warn('Service worker not ready:', error);
        });
      }
    };
    
    const handleOffline = () => {
      console.log('Network status: Offline');
      setIsOnline(false);
      
      // Update offline container visibility safely
      try {
        document.body.classList.add('offline');
      } catch (error) {
        console.warn('Error updating body class:', error);
      }
    };

    // Set initial state safely
    try {
      if (!navigator.onLine) {
        console.log('Initial network status: Offline');
        document.body.classList.add('offline');
      } else {
        console.log('Initial network status: Online');
        document.body.classList.remove('offline');
      }
    } catch (error) {
      console.warn('Error setting initial network state:', error);
    }

    // Add event listeners with error handling
    try {
      window.addEventListener('online', handleOnline);
      window.addEventListener('offline', handleOffline);
    } catch (error) {
      console.error('Error adding network event listeners:', error);
    }

    return () => {
      try {
        window.removeEventListener('online', handleOnline);
        window.removeEventListener('offline', handleOffline);
      } catch (error) {
        console.warn('Error removing network event listeners:', error);
      }
    };
  }, []);

  return (
    <div className={`fixed bottom-16 right-4 z-40 p-2 rounded-full shadow-lg transition-all duration-300 ${
      isOnline 
        ? 'bg-green-500 text-white' 
        : 'bg-amber-500 text-white animate-pulse'
    }`}>
      <div className="flex items-center justify-center">
        {isOnline ? (
          <Wifi className="w-4 h-4" />
        ) : (
          <WifiOff className="w-4 h-4" />
        )}
      </div>
    </div>
  );
};

// Main component with error boundary
const NetworkStatus: React.FC = () => {
  return (
    <NetworkStatusErrorBoundary>
      <NetworkStatusComponent />
    </NetworkStatusErrorBoundary>
  );
};

export default NetworkStatus;
